import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

// Sacred Background Component - Visual effects only
// Sensor integration removed (was part of deleted appropriative code)
const SacredBackground: React.FC = () => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const animationRef = useRef<number>();

  useEffect(() => {
    if (!mountRef.current) return;

    // Track mount state for cleanup
    let isMounted = true;

    // Sacred Scene Setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Sacred Camera - Perspective for divine vision
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 5;

    // Sacred Renderer - Nearest neighbor crisp clarity
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: false // Disabled for nearest neighbor crisp clarity
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    rendererRef.current = renderer;
    mountRef.current.appendChild(renderer.domElement);

    // Sacred Geometry - Fibonacci Spiral (Infinite potential 00)
    const fibonacciSpiral = createFibonacciSpiral();
    scene.add(fibonacciSpiral);

    // Sacred Mandala - Unity and completion (R=9)
    const sacredMandala = createSacredMandala();
    scene.add(sacredMandala);

    // Sacred Particles - Cosmic dust
    const particles = createSacredParticles();
    scene.add(particles);

    // Sacred Lights - Divine illumination
    const ambientLight = new THREE.AmbientLight(0x8b5cf6, 0.3);
    scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0x6366f1, 1, 100);
    pointLight.position.set(0, 0, 10);
    scene.add(pointLight);

    // Sacred Animation Loop - Time-based animations
    const animate = () => {
      if (!isMounted || !mountRef.current) return; // Prevent animation after unmount
      animationRef.current = requestAnimationFrame(animate);

      const time = Date.now() * 0.001;

      // Animate Fibonacci spiral - Smooth rotation and scale
      if (fibonacciSpiral) {
        // Gentle rotation
        const rotationSpeed = 0.1;
        fibonacciSpiral.rotation.z = time * rotationSpeed;
        
        // Subtle tilt animation
        fibonacciSpiral.rotation.x = Math.sin(time * 0.2) * 0.1;
        fibonacciSpiral.rotation.y = Math.cos(time * 0.3) * 0.1;
        
        // Gentle scale animation
        const scale = 1 + Math.sin(time * 0.3) * 0.1;
        fibonacciSpiral.scale.setScalar(scale);
      }

      // Animate Sacred Mandala - Rotation and pulsing
      if (sacredMandala) {
        // Gentle rotation
        sacredMandala.rotation.y = time * 0.2;
        
        // Subtle tilt
        sacredMandala.rotation.x = Math.cos(time * 0.03) * 0.05;
        
        // Gentle pulse animation
        const pulse = Math.sin(time * 0.5) * 0.1 + 1;
        sacredMandala.scale.setScalar(pulse);
      }

      // Animate Particles - Gentle movement
      if (particles) {
        // Gentle rotation
        particles.rotation.y = time * 0.05;
        particles.rotation.x = Math.sin(time * 0.02) * 0.02;
        
        // Subtle breathing effect
        const spread = 1 + Math.sin(time * 0.4) * 0.05;
        particles.scale.setScalar(spread);
      }

      // Animate Lights - Time-based color cycling
      pointLight.intensity = 0.5 + Math.sin(time * 2) * 0.2;
      
      // Slow color cycle through purple/indigo spectrum
      const hue = (0.7 + Math.sin(time * 0.1) * 0.1) % 1;
      pointLight.color.setHSL(hue, 0.7, 0.6);
      
      // Gentle ambient light variation
      ambientLight.intensity = 0.2 + Math.sin(time * 0.5) * 0.1;

      renderer.render(scene, camera);
    };

    animate();

    // Sacred Resize Handler
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Sacred Cleanup - prevent memory leaks
    return () => {
      isMounted = false; // Stop animation loop
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = undefined;
      }
      
      // Remove all objects from scene and dispose
      if (scene) {
        // Dispose all geometries and materials recursively
        scene.traverse((object) => {
          if (object instanceof THREE.Mesh) {
            if (object.geometry) object.geometry.dispose();
            if (object.material) {
              if (Array.isArray(object.material)) {
                object.material.forEach(mat => mat.dispose());
              } else {
                object.material.dispose();
              }
            }
          }
        });
        scene.clear();
      }
      
      // Store current mount point for cleanup
      const currentMount = mountRef.current;
      if (currentMount && renderer.domElement) {
        try {
          currentMount.removeChild(renderer.domElement);
        } catch (e) {
          // Element may already be removed
        }
      }
      renderer.dispose();
      
      // Clear refs
      rendererRef.current = undefined;
      sceneRef.current = undefined;
    };
  }, []);

  return (
    <div
      ref={mountRef}
      className="fr00t-background"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -1,
        pointerEvents: 'none',
        imageRendering: 'crisp-edges' // Nearest neighbor crisp clarity
      }}
    />
  );
};

// Sacred Fibonacci Spiral Creation
// Infinite potential and natural growth (00)
const createFibonacciSpiral = (): THREE.Group => {
  const group = new THREE.Group();
  const goldenRatio = 1.618033988749895;
  const goldenAngle = 137.5 * (Math.PI / 180);
  
  const points: THREE.Vector3[] = [];
  const colors: THREE.Color[] = [];
  
  for (let i = 0; i < 100; i++) {
    const radius = Math.pow(goldenRatio, i * 0.1) * 0.1;
    const angle = i * goldenAngle;
    
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    const z = Math.sin(i * 0.1) * 0.5;
    
    points.push(new THREE.Vector3(x, y, z));
    
    // Sacred color based on position
    const hue = (angle * 180 / Math.PI) % 360;
    colors.push(new THREE.Color().setHSL(hue / 360, 0.8, 0.6));
  }
  
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(
    colors.flatMap(color => [color.r, color.g, color.b]),
    3
  ));
  
  const material = new THREE.PointsMaterial({
    size: 0.02,
    vertexColors: true,
    transparent: true,
    opacity: 0.8
  });
  
  const spiral = new THREE.Points(geometry, material);
  group.add(spiral);
  
  return group;
};

// Sacred Mandala Creation
// Unity and completion (R=9)
const createSacredMandala = (): THREE.Group => {
  const group = new THREE.Group();
  const radius = 2;
  const petals = 8; // Sacred number 8
  
  // Create mandala petals
  for (let i = 0; i < petals; i++) {
    const angle = (i * 360 / petals) * (Math.PI / 180);
    
    // Sacred petal geometry
    const petalGeometry = new THREE.ConeGeometry(0.1, 0.5, 6);
    const petalMaterial = new THREE.MeshPhongMaterial({
      color: new THREE.Color().setHSL(i / petals, 0.7, 0.6),
      transparent: true,
      opacity: 0.6
    });
    
    const petal = new THREE.Mesh(petalGeometry, petalMaterial);
    petal.position.set(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius,
      0
    );
    petal.rotation.z = angle;
    
    group.add(petal);
  }
  
  // Sacred center circle
  const centerGeometry = new THREE.CircleGeometry(0.3, 32);
  const centerMaterial = new THREE.MeshPhongMaterial({
    color: 0x8b5cf6,
    transparent: true,
    opacity: 0.8
  });
  
  const center = new THREE.Mesh(centerGeometry, centerMaterial);
  center.position.z = 0.1;
  group.add(center);
  
  return group;
};

// Sacred Particles Creation
// Cosmic dust and infinite potential
const createSacredParticles = (): THREE.Group => {
  const group = new THREE.Group();
  const particleCount = 1000;
  
  const positions = new Float32Array(particleCount * 3);
  const colors = new Float32Array(particleCount * 3);
  
  for (let i = 0; i < particleCount; i++) {
    // Random positions in a sphere
    const radius = Math.random() * 10;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(Math.random() * 2 - 1);
    
    positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = radius * Math.cos(phi);
    
    // Sacred colors
    const hue = Math.random();
    const color = new THREE.Color().setHSL(hue, 0.7, 0.6);
    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }
  
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  
  const material = new THREE.PointsMaterial({
    size: 0.01,
    vertexColors: true,
    transparent: true,
    opacity: 0.6
  });
  
  const particles = new THREE.Points(geometry, material);
  group.add(particles);
  
  return group;
};

export default SacredBackground;

